/**
 * @title The add dialog - A wholesale category
 * @description The item to tradein will have this as category
 */
import {CSSProperties, FC} from "react";
import {makeStyles} from "@mui/styles";
import {GlobalLayout, StyledTypography} from "../../../../providers/layout/LayoutProvider";
import clsx from "clsx";
import {useRecoilValue} from "recoil";
import {WholeSaleCategory} from "../../../../Queries";
import {LocaleAtom} from "../../../../providers/localization/LocalizationProvider";
import {Currency} from "../../../../components/Currency";
import {ImageServerUrl} from "../../../../Settings";

type CategoryProps = {
   category: WholeSaleCategory
   selected: boolean
   onSelect: (category: WholeSaleCategory) => void
   wider: boolean
   showSection?: boolean
}

export const Category: FC<CategoryProps> = (props) => {
   const globalLayout = useRecoilValue(GlobalLayout);
   if (props.category.iconId && !globalLayout.disableIcons) {
      return <CategoryIcon {...props}/>
   } else {
      return <CategoryText {...props}/>
   }
}

export const CategoryText: FC<CategoryProps> = ({category, selected, onSelect, wider, showSection}) => {
   const locale = useRecoilValue(LocaleAtom);
   const classes = useStylesText({wider});
   const globalLayout = useRecoilValue(GlobalLayout);

   // Todo, make this prettier / use the localization framework
   let categoryName = category.name;
   if ((category.nameLocalized || []).length > 0) {
      const categoryLocalized = category.nameLocalized?.find(loc => loc?.locale === locale);
      if (categoryLocalized) {
         categoryName = categoryLocalized.value;
      }
   }

   return <div className={clsx(classes.category, selected && classes.selected)} onClick={() => {
      onSelect(category);
   }} style={{
      borderColor: selected ? globalLayout.widgets?.selectionColor || undefined : globalLayout.widgets?.categoryBorderColor || undefined,
      backgroundColor: selected ? globalLayout.widgets?.selectionColor || undefined : undefined,
      borderRadius: "2px",
   }}>
      <div className={classes.categoryText}>
         <div>
            {showSection &&
               <StyledTypography type={"main"} variant={"subtitle1"} style={{
                  opacity: 0.6,
                  color: selected ? globalLayout.widgets?.selectionTextColor || undefined : undefined
               }}>
                  {category.section}
               </StyledTypography>
            }
            <StyledTypography type={"main"} variant={"body1"} style={{
               color: selected ? globalLayout.widgets?.selectionTextColor || undefined : undefined
            }} dangerouslySetInnerHTML={{__html: categoryName}}/>
         </div>
         <div>
            <StyledTypography type={"main"} variant={"subtitle1"} className={classes.categoryValue} style={{
               opacity: 0.7,
               color: selected ? globalLayout.widgets?.selectionTextColor || undefined : undefined
            }}>
               <Currency amount={category.value}/>
            </StyledTypography>
         </div>
      </div>
   </div>
}

const useStylesText = makeStyles({
   category: {
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      width: '160px',
      cursor: 'pointer',
      justifyContent: 'start',
      border: '1px solid #FF5376',
      flex: (props: { wider: boolean }) => props?.wider ? '1 1 40%' : '1 1 30%'
   },
   categoryText: {
      borderRadius: '0%',
      width: '100%',
      minHeight: '60px',
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'center',
      alignItems: 'center',
      textAlign: 'center'
   },
   selected: {
      backgroundColor: '#9DF7E5',
      border: '1px solid #9DF7E5',
   },
   categoryValue: {
      whiteSpace: 'nowrap'
   }
});

export const CategoryIcon: FC<CategoryProps> = ({category, selected, onSelect, wider}) => {
   const classes = useStylesIcon();
   const globalLayout = useRecoilValue(GlobalLayout);

   const iconSize = globalLayout.widgets?.categoryIconSize || 55
   const iconPadding = globalLayout.widgets?.categoryIconPadding || 10
   const iconSizeStyle: CSSProperties = {
      width: iconSize + 'px',
      height: iconSize + 'px',
      padding: iconPadding + 'px',
      boxSizing: 'content-box'
   }

   return <div className={clsx(classes.category, 'c2s-category-container')} onClick={() => {
      onSelect(category);
   }}>
      <div className={clsx(classes.categoryImage, selected && classes.selected, 'c2s-category-inner')}
           style={{
              borderColor: selected ? globalLayout.widgets?.selectionColor || undefined : globalLayout.widgets?.categoryBorderColor || undefined,
              borderRadius: globalLayout.widgets?.categoryRadius || undefined,
              backgroundColor: selected ? globalLayout.widgets?.selectionColor || undefined : undefined,
              borderWidth: '2px'
           }}
      >
         {category.iconId && <div className={clsx(classes.imageContainer, 'c2s-category-image-container')}><img
            className={clsx(classes.image, 'c2s-category-image')}
            src={ImageServerUrl + '/' + category.iconId}
            alt={category.name}
            title={category.name}
            style={!globalLayout.widgets?.categoryRadius ? {
               borderColor: selected ? globalLayout.widgets?.selectionColor || undefined : globalLayout.widgets?.categoryBorderColor || undefined,
               borderRadius: globalLayout.widgets?.categoryRadius || undefined,
               backgroundColor: selected ? globalLayout.widgets?.selectionColor || undefined : undefined,
               ...iconSizeStyle
            } : {...iconSizeStyle}}
         /></div>}
      </div>
      <StyledTypography type={"main"} variant={"subtitle1"} className={classes.categoryText}>
         {category.name}
      </StyledTypography>
      <StyledTypography type={"main"} variant={"subtitle1"} className={classes.categoryValue} sx={{}}>
         <Currency amount={category.value}/>
      </StyledTypography>
   </div>
}

const useStylesIcon = makeStyles({
   category: {
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      width: '140px',
      cursor: 'pointer',
      justifyContent: 'start',
      paddingLeft: '10px',
      paddingRight: '10px',
      flex: '1 1 27%'
   },
   '@media only screen and (max-width: 600px)': {
      category: {
         flex: '1 1 35%'
      },
   },
   categoryImage: {
      border: '1px solid #FF5376',
      borderRadius: '50%',
      display: 'flex',
   },
   selected: {
      backgroundColor: '#9DF7E5',
      border: '1px solid #9DF7E5',
   },
   imageContainer: {
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center'
   },
   image: {
      alignSelf: 'auto'
   },
   categoryText: {
      textAlign: 'center',
      marginTop: '5px'
   },
   categoryValue: {
      whiteSpace: 'nowrap'
   }
});
